/**
 * OPERATOR FEATURES — one flag, one commit, two ways to turn it off.
 *
 * <h2>How to revert</h2>
 *
 * Everything added for the institutional-operator set is behind {@link operatorFeaturesEnabled}
 * and lives in files whose names begin `operator` or `trendPulsePhase`/`validationLines`. Three
 * exits, cheapest first:
 *
 * <ol>
 *   <li><b>At runtime, no rebuild.</b> In the browser console:
 *       `localStorage.setItem('astrocharts.operatorFeatures', 'off')` then reload. Every toolbar
 *       button disappears and no phase ribbon is drawn. `'on'` puts it back; removing the key
 *       returns to {@link DEFAULT_ENABLED}.</li>
 *   <li><b>For everyone, one line.</b> Set `DEFAULT_ENABLED` below to `false` and redeploy.</li>
 *   <li><b>Gone entirely.</b> `git revert <the operator-features commit>`. The whole set is one
 *       commit, and every file it adds is new, so the revert touches nothing that existed
 *       before it.</li>
 * </ol>
 *
 * <p>The runtime toggle exists because these features are being judged by being looked at. A flag
 * that needs a build to flip is a flag nobody flips while forming an opinion — and the opinion is
 * the point.
 *
 * <p><b>Keep this contract if you extend the set</b>: gate every entry point through the function
 * below rather than through a captured boolean. The value is read per call, so a console toggle
 * takes effect on the next render instead of at the next reload.
 */

/** What the flag means when nothing has been set in localStorage. */
export const DEFAULT_ENABLED = true;

export const OPERATOR_FEATURES_KEY = 'astrocharts.operatorFeatures';

/**
 * Whether the operator feature set is on for this browser.
 *
 * <p>Wrapped in try/catch because `localStorage` throws rather than returning null in a few real
 * configurations — Safari's private mode historically, and any embedding that denies storage. A
 * feature flag that can crash the app it gates is worse than the feature it hides.
 */
export function operatorFeaturesEnabled() {
  try {
    const setting = window.localStorage.getItem(OPERATOR_FEATURES_KEY);
    if (setting === 'off') return false;
    if (setting === 'on') return true;
  } catch (err) {
    /* storage unavailable — fall through to the default */
  }
  return DEFAULT_ENABLED;
}
